export const BILLING_PENDING_CONDITION_TYPE = "billing.pending_actual_tonnage";
export const NOTIFICATION_ACTION_TYPE = "notification.in_app";
export const MISSING_ACTUAL_TONNAGE_CATEGORIES = Object.freeze(["missing_loaded", "missing_delivered"]);

const BILLING_MESSAGE_PARTS = [
  ["missing_loaded", "Réel chargé manquant : {{ billing.missing_loaded_count }}"],
  ["missing_delivered", "Réel livré manquant : {{ billing.missing_delivered_count }}"],
  ["ready_to_approve", "Prêtes à approuver : {{ billing.ready_to_approve_count }}"],
  ["blocked", "Bloquées autrement : {{ billing.blocked_count }}"],
];

export function normalizeBillingCategories(categories = []) {
  const sourceCategories = Array.isArray(categories) ? categories : [];
  const selectedCategories = new Set(sourceCategories);
  if (MISSING_ACTUAL_TONNAGE_CATEGORIES.some((category) => selectedCategories.has(category))) {
    MISSING_ACTUAL_TONNAGE_CATEGORIES.forEach((category) => selectedCategories.add(category));
  }
  const knownCategories = new Set(BILLING_MESSAGE_PARTS.map(([category]) => category));
  const orderedKnownCategories = BILLING_MESSAGE_PARTS
    .map(([category]) => category)
    .filter((category) => selectedCategories.has(category));
  const additionalCategories = sourceCategories.filter((category, index) => (
    typeof category === "string"
      && !knownCategories.has(category)
      && sourceCategories.indexOf(category) === index
  ));
  return [...orderedKnownCategories, ...additionalCategories];
}

export function toggleBillingCategoryGroup(categories, categoryGroup) {
  const normalizedCategories = normalizeBillingCategories(categories);
  const selectedCategories = new Set(normalizedCategories);
  const group = [...new Set(Array.isArray(categoryGroup) ? categoryGroup : [])];
  const isSelected = group.length > 0 && group.every((category) => selectedCategories.has(category));
  group.forEach((category) => {
    if (isSelected) selectedCategories.delete(category);
    else selectedCategories.add(category);
  });
  return normalizeBillingCategories([...selectedCategories]);
}

export function buildBillingNotificationMessage(categories = [], lookbackDays = 1) {
  const selectedCategories = new Set(normalizeBillingCategories(categories));
  const resolvedLookbackDays = Number.isInteger(Number(lookbackDays)) && Number(lookbackDays) >= 1 && Number(lookbackDays) <= 4
    ? Number(lookbackDays)
    : 1;
  const periodLabel = resolvedLookbackDays === 1
    ? "de la journée d’hier"
    : `des ${resolvedLookbackDays} dernières journées terminées`;
  const introduction = `{{ billing.total_count }} commande(s) ${periodLabel} attendent une intervention.`;
  const details = BILLING_MESSAGE_PARTS
    .filter(([category]) => selectedCategories.has(category))
    .map(([, message]) => message);
  return details.length > 0 ? `${introduction} ${details.join(" · ")}.` : introduction;
}

export function initializeNotificationMessageState(
  action,
  { categories, lookbackDays, automatic = false },
) {
  if (action?.type !== NOTIFICATION_ACTION_TYPE) return action;
  const generatedBody = buildBillingNotificationMessage(categories, lookbackDays);
  const isAutomatic = automatic || action.body_template === generatedBody;
  return {
    ...action,
    _messageTemplateMode: isAutomatic ? "automatic" : "custom",
    _generatedBodyTemplate: generatedBody,
    _pendingGeneratedBodyTemplate: null,
  };
}

export function applyBillingMessageCriteria(
  form,
  { categories = form.billingCategories, lookbackDays = form.billingLookbackDays } = {},
) {
  const normalizedCategories = normalizeBillingCategories(categories);
  const generatedBody = buildBillingNotificationMessage(normalizedCategories, lookbackDays);
  return {
    ...form,
    billingCategories: normalizedCategories,
    billingLookbackDays: lookbackDays,
    actions: form.actions.map((action) => {
      if (action.type !== NOTIFICATION_ACTION_TYPE) return action;
      const isAutomatic = action._messageTemplateMode === "automatic";
      if (isAutomatic) {
        return {
          ...action,
          body_template: generatedBody,
          _generatedBodyTemplate: generatedBody,
          _pendingGeneratedBodyTemplate: null,
        };
      }
      return {
        ...action,
        _messageTemplateMode: "custom",
        _generatedBodyTemplate: generatedBody,
        _pendingGeneratedBodyTemplate: generatedBody,
      };
    }),
  };
}

export function notificationBodyEditPatch(action, bodyTemplate) {
  return {
    body_template: bodyTemplate,
    _messageTemplateMode: bodyTemplate === action._generatedBodyTemplate ? "automatic" : "custom",
    _pendingGeneratedBodyTemplate: null,
  };
}

export function acceptGeneratedNotificationBodyPatch(action) {
  return {
    body_template: action._pendingGeneratedBodyTemplate || action._generatedBodyTemplate,
    _messageTemplateMode: "automatic",
    _pendingGeneratedBodyTemplate: null,
  };
}

export function keepCustomNotificationBodyPatch() {
  return {
    _messageTemplateMode: "custom",
    _pendingGeneratedBodyTemplate: null,
  };
}

export function buildBillingPendingCondition(categories, lookbackDays) {
  const normalizedCategories = normalizeBillingCategories(categories);
  const resolvedLookbackDays = Number(lookbackDays);
  if (normalizedCategories.length === 0) {
    throw new Error("Sélectionnez au moins une catégorie de tonnage.");
  }
  if (!Number.isInteger(resolvedLookbackDays) || resolvedLookbackDays < 1 || resolvedLookbackDays > 4) {
    throw new Error("Choisissez une période de 1 à 4 journées terminées.");
  }
  return {
    type: BILLING_PENDING_CONDITION_TYPE,
    categories: normalizedCategories,
    through: "yesterday",
    lookback_days: resolvedLookbackDays,
  };
}

export function splitAutomationTargets(targets, conditionType) {
  const users = Array.isArray(targets?.users) ? targets.users : [];
  const roles = Array.isArray(targets?.roles) ? targets.roles : [];
  if (conditionType !== BILLING_PENDING_CONDITION_TYPE) {
    return { eligibleUsers: users, eligibleRoles: roles, ineligibleUsers: [], ineligibleRoles: [] };
  }
  return {
    eligibleUsers: users.filter((user) => user.billing_pending_eligible === true),
    eligibleRoles: roles.filter((role) => role.billing_pending_eligible === true),
    ineligibleUsers: users.filter((user) => user.billing_pending_eligible !== true),
    ineligibleRoles: roles.filter((role) => role.billing_pending_eligible !== true),
  };
}

export function pruneIneligibleNotificationRecipients(form, targets) {
  if (form.conditionType !== BILLING_PENDING_CONDITION_TYPE) {
    return { form, removedRecipients: [] };
  }
  const { eligibleUsers, eligibleRoles } = splitAutomationTargets(targets, form.conditionType);
  const eligibleUserIds = new Set(eligibleUsers.map((user) => user.user_id));
  const eligibleRoleKeys = new Set(eligibleRoles.map((role) => role.role_key));
  const removedRecipients = [];
  const actions = form.actions.map((action) => {
    if (action.type !== NOTIFICATION_ACTION_TYPE) return action;
    const userIds = action.recipients?.user_ids || [];
    const roleKeys = action.recipients?.role_keys || [];
    const nextUserIds = userIds.filter((userId) => eligibleUserIds.has(userId));
    const nextRoleKeys = roleKeys.filter((roleKey) => eligibleRoleKeys.has(roleKey));
    removedRecipients.push(
      ...userIds.filter((userId) => !eligibleUserIds.has(userId)).map((value) => ({ group: "user", value })),
      ...roleKeys.filter((roleKey) => !eligibleRoleKeys.has(roleKey)).map((value) => ({ group: "role", value })),
    );
    return {
      ...action,
      recipients: { user_ids: nextUserIds, role_keys: nextRoleKeys },
    };
  });
  if (removedRecipients.length === 0) {
    return { form: { ...form, actions }, removedRecipients };
  }
  const recipientLabel = removedRecipients.length === 1 ? "destinataire non admissible a été retiré" : "destinataires non admissibles ont été retirés";
  return {
    form: {
      ...form,
      actions,
      recipientEligibilityNotice: `${removedRecipients.length} ${recipientLabel} du brouillon après la vérification des droits.`,
    },
    removedRecipients,
  };
}

export function notificationActionsHaveEligibleRecipients(form, targets) {
  const notificationActions = form.actions.filter((action) => action.type === NOTIFICATION_ACTION_TYPE);
  if (notificationActions.length === 0) return true;
  if (form.conditionType !== BILLING_PENDING_CONDITION_TYPE) {
    return notificationActions.every((action) => (
      (action.recipients?.user_ids?.length || 0) + (action.recipients?.role_keys?.length || 0) > 0
    ));
  }
  const { eligibleUsers, eligibleRoles } = splitAutomationTargets(targets, form.conditionType);
  const eligibleUserIds = new Set(eligibleUsers.map((user) => user.user_id));
  const eligibleRoleKeys = new Set(eligibleRoles.map((role) => role.role_key));
  return notificationActions.every((action) => (
    (action.recipients?.user_ids || []).some((userId) => eligibleUserIds.has(userId))
      || (action.recipients?.role_keys || []).some((roleKey) => eligibleRoleKeys.has(roleKey))
  ));
}

export function stripAutomationActionClientState(action) {
  const {
    _key,
    _messageTemplateMode,
    _generatedBodyTemplate,
    _pendingGeneratedBodyTemplate,
    ...payloadAction
  } = action;
  return payloadAction;
}
